import React, { useEffect, useState } from 'react';
import { Save, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { TextArea, SelectField } from './AdminFields';

const ISSUE = { repair: 'Repair', maintenance: 'Maintenance', parts: 'Parts', technical: 'Technical' };
const STATUS = [['new', 'New'], ['in_progress', 'In progress'], ['waiting_parts', 'Waiting on parts'], ['completed', 'Completed'], ['closed', 'Closed']];

const Row = ({ r, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ status: r.status, admin_notes: r.admin_notes || '' });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/service-requests/${r.id}`, f);
            toast.success(`${r.request_number} saved`);
            onSaved();
        } catch {
            toast.error('Could not save — please try again');
        }
        setBusy(false);
    };
    return (
        <div className="border-b border-white/10" data-testid={`admin-service-${r.id}`}>
            <button onClick={() => setOpen(!open)} className="w-full p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-center text-left hover:bg-white/5" data-testid={`admin-service-toggle-${r.id}`}>
                <span className="font-display font-bold text-white flex items-center gap-2">{r.machine_down && <AlertTriangle className="h-4 w-4 text-red-600" />}{r.request_number}</span>
                <span className="text-sm text-white/70 truncate">{r.name}</span>
                <span className="text-sm text-white/70 truncate md:col-span-2">{r.machine_model} · {ISSUE[r.issue_type]}</span>
                <span className="text-sm text-white/50">{new Date(r.created_at).toLocaleDateString()}</span>
                <span className="label-caps text-xs text-white/60" data-testid={`admin-service-status-label-${r.id}`}>{STATUS.find((s) => s[0] === r.status)?.[1]}</span>
            </button>
            {open && (
                <div className="p-5 bg-brand-bg grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`admin-service-details-${r.id}`}>
                    <div className="text-sm text-white/70 space-y-1">
                        <div>{r.name}{r.company ? ` · ${r.company}` : ''}</div>
                        <div><a href={`mailto:${r.email}`} className="text-brand-primary">{r.email}</a> · {r.phone} · prefers {r.preferred_contact}</div>
                        <div>{[r.city, r.state].filter(Boolean).join(', ')}</div>
                        <div>SN {r.serial_number || '—'} · {r.purchased_from_us ? 'Bought from us' : 'Not bought from us / unsure'}</div>
                        <p className="pt-2 whitespace-pre-line text-white/80">{r.description}</p>
                        {r.photos?.length > 0 && (
                            <div className="pt-2 flex gap-2 flex-wrap">
                                {r.photos.map((p, i) => <a key={p} href={p} target="_blank" rel="noreferrer"><img src={p} alt={`Photo ${i + 1}`} className="w-20 h-20 object-cover border border-white/10" /></a>)}
                            </div>
                        )}
                    </div>
                    <div className="space-y-3">
                        <SelectField label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS} testid={`admin-service-status-${r.id}`} />
                        <TextArea label="Internal notes" rows={3} value={f.admin_notes} onChange={(v) => setF({ ...f, admin_notes: v })} testid={`admin-service-notes-${r.id}`} />
                        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-brand-ink font-bold px-4 py-2.5 label-caps" data-testid={`admin-service-save-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ServiceRequestsAdmin = () => {
    const [items, setItems] = useState([]);
    const load = () => api.get('/admin/service-requests').then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="admin-service-requests">
            <h2 className="font-display font-black uppercase text-3xl mb-2">Service requests</h2>
            <p className="text-white/50 text-sm mb-6">Repair, maintenance, parts and technical requests from the Service & Support page. Page text is editable in the Pages tab.</p>
            <div className="border border-white/10" data-testid="admin-service-list">
                {items.length === 0 && <div className="p-6 text-white/50">No service requests yet.</div>}
                {items.map((r) => <Row key={r.id} r={r} onSaved={load} />)}
            </div>
        </div>
    );
};

export default ServiceRequestsAdmin;
